import { Notify } from 'quasar';
import { apolloService } from '@/apollo/apolloRequests';
import { mutations } from '@/apollo';
import { FileAccStatus, type FileRejectionReason } from '@/generated/graphql.ts';
import { sentry } from '@/helpers/sentry.ts';

interface ChangeFileStatusParams {
  fileIds: string[];
  accStatusId: FileAccStatus;
  rejectionReasonId?: FileRejectionReason | null;
  rejectionComment?: string | null;
}

export function useFileStatusChange() {
  function logChangeFileStatusError(err: unknown, params: ChangeFileStatusParams) {
    sentry().sendError(
      err instanceof Error ? err : new Error('Unknown error'),
      JSON.stringify({
        method: 'changeFilesStatus',
        params,
        backendError: err,
      }),
      'changeFilesStatus_error',
    );
  }

  const updateAccountingApprovalStatus = async ({
    fileIds,
    accStatusId,
    rejectionReasonId = null,
    rejectionComment = null,
  }: ChangeFileStatusParams) => {
    if (!fileIds || fileIds.length === 0) return false;

    try {
      await apolloService.makeApolloRequest('mutation', mutations.CHANGE_FILES_STATUS, {
        fileIds,
        accStatusId,
        rejectionReasonId,
        rejectionComment,
      });

      const action = accStatusId === FileAccStatus.Confirmed ? 'confirmed' : 'denied';
      Notify.create({
        message: fileIds.length === 1
          ? `File has been ${action} successfully`
          : `${fileIds.length} files have been ${action} successfully`,
        color: 'positive',
        position: 'top',
      });

      return true;
    } catch (err) {
      console.error(err);
      logChangeFileStatusError(err, { fileIds, accStatusId, rejectionReasonId, rejectionComment });
      Notify.create({
        message: 'Failed to change file status. Please try again.',
        color: 'negative',
        position: 'top',
      });
      return false;
    }
  };

  const confirmAccountingFile = async (fileIds: string[]) => {
    return updateAccountingApprovalStatus({
      fileIds,
      accStatusId: FileAccStatus.Confirmed,
    });
  };

  return {
    updateAccountingApprovalStatus,
    confirmAccountingFile,
  };
}
